// ============================================================================
// /bids/:bidId — one bid, five steps (design reference screens 2–6):
// Start · Building · Services · Pricing · Review. Screens 7–10 (documents,
// sending, the signer, signed → account) are the second item.
// ============================================================================

import { Link, useParams, useSearchParams } from 'react-router-dom';
import { EmptyState, ErrorState, PageHeader, Skeleton } from '../../shared/components';
import { fetchAccounts, fetchBid, fetchIsExecutive, fetchMarkets, fetchParents, STATUS_WORDS, typeLabel, type BidRow, type Option } from './api';
import { Section } from './fields';
import { StartForm } from './start-form';
import { BuildingForm } from './building-form';
import { ServicesPanel } from './services-panel';
import { PricingPanel } from './pricing-panel';
import { ReviewPanel } from './review-panel';
import { useBidLoad } from './use-bid-load';

const STEPS = [
  { key: 'start', label: 'Start' },
  { key: 'building', label: 'Building' },
  { key: 'services', label: 'Services' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'review', label: 'Review' },
] as const;
type Step = (typeof STEPS)[number]['key'];

export default function BidDetail() {
  const { bidId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const step = (STEPS.find((s) => s.key === params.get('step'))?.key ?? 'review') as Step;
  const go = (s: Step) => setParams({ step: s });

  const { data: bid, loading, error, refetch } = useBidLoad<BidRow | null>(() => fetchBid(bidId), [bidId], null, step === 'review' ? 5000 : undefined);
  const { data: lists } = useBidLoad<{ markets: Option[]; parents: Option[]; accounts: Option[]; exec: boolean }>(
    async () => {
      const [markets, parents, accounts, exec] = await Promise.all([fetchMarkets(), fetchParents(), fetchAccounts(), fetchIsExecutive()]);
      return { markets, parents, accounts, exec };
    },
    [],
    { markets: [], parents: [], accounts: [], exec: false },
  );

  if (loading) return <div className="p-6"><Skeleton className="h-64 w-full" /></div>;
  if (error) return <div className="p-6"><ErrorState body={error} onRetry={refetch} /></div>;
  if (!bid) return <div className="p-6"><EmptyState title="There is no bid with that number." cta={<Link className="underline" to="/bids">Back to Bids</Link>} /></div>;

  const locked = bid.status !== 'Draft' && bid.status !== 'Submitted';
  const where = [bid.prospect_city, bid.prospect_state].filter(Boolean).join(', ');

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader
        eyebrow={<Link to="/bids" className="hover:underline">Bids</Link> as unknown as string}
        title={bid.facility_name}
        meta={`${typeLabel(bid.facility_type)}${where ? ` · ${where}` : ''}${bid.market_region ? ` · ${bid.market_region}` : ''} · ${STATUS_WORDS[bid.status] ?? bid.status}`}
      />
      <nav className="flex flex-wrap gap-1" aria-label="Bid steps">
        {STEPS.map((s, i) => (
          <button key={s.key} type="button" onClick={() => go(s.key)}
            className={`rounded-full px-3 py-1 text-sm ${step === s.key ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'border border-tcs-border text-tcs-text-secondary hover:bg-tcs-surface'}`}>
            {i + 1}. {s.label}
          </button>
        ))}
      </nav>
      {step === 'start' && (
        <Section title="Who are we bidding?">
          <StartForm bid={bid} markets={lists.markets} parents={lists.parents} accounts={lists.accounts} locked={locked} onSaved={() => go('building')} />
        </Section>
      )}
      {step === 'building' && (
        <Section title="Tell us about the building"><BuildingForm bid={bid} locked={locked} onPriced={() => go('services')} /></Section>
      )}
      {step === 'services' && (
        <Section title="What are we quoting?"><ServicesPanel bid={bid} onNext={() => go('pricing')} /></Section>
      )}
      {step === 'pricing' && (
        <Section title="The number">
          {bid.recommended_monthly == null && bid.total_sqft == null && bid.chair_count == null
            ? <p className="text-sm text-tcs-text-muted">Tell us about the building first — the price comes from it.</p>
            : <PricingPanel bid={bid} onNext={() => go('review')} />}
        </Section>
      )}
      {step === 'review' && <ReviewPanel bid={bid} isExecutive={lists.exec} />}
    </div>
  );
}
